<template>
    <div
        class="patsnap-biz-ai-history-item relative"
        :class="{
            'mt-4': menu?.groupFirst,
        }"
    >
        <ElTooltip
            :effect="hasChildren ? 'light' : 'dark'"
            :showArrow="false"
            :offset="16"
            :disabled="opened"
            placement="right"
            popperClass="pt-ui-tooltip !px-0 !py-2"
        >
            <template #content>
                <SubComponentPopover :menu="menu">
                    <template #footer="scopes">
                        <slot name="footer" v-bind="scopes" />
                    </template>
                </SubComponentPopover>
            </template>
            <div
                v-if="!hasChildren"
                class="flex flex-row justify-center items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-hover"
                :class="{ 'active-menu-item': parentActive }"
                @click="toggleMenu"
            >
                <div v-if="customIconComponent || menu.icon" class="flex flex-row relative">
                    <component :is="customIconComponent" v-if="customIconComponent" class="patsnap-biz-icon shrink-0 text-2xl !fill-white" />
                    <PtIcon v-else-if="menu.icon" class="shrink-0 text-2xl" :icon="menu.icon" />
                </div>
                <span v-if="opened" class="flex-1">{{ menu.title }}</span>
            </div>
            <div
                v-else
                class="flex flex-row items-center gap-2 py-2 mt-2 px-4 cursor-pointer hover:bg-gray-hover"
                :class="{ 'active-menu-item': parentActive, 'justify-center': !opened }"
                @click="toggleMenu"
                @mouseenter="showTriangle = true"
                @mouseleave="showTriangle = false"
            >
                <div v-if="!opened" class="flex flex-row relative">
                    <!-- 自定义 SVG 图标（以 Icon 开头） -->
                    <component :is="customIconComponent" v-if="customIconComponent" class="patsnap-biz-icon shrink-0 text-2xl !fill-white" />
                    <PtIcon v-else-if="menu.icon" class="shrink-0 text-2xl" :icon="menu.icon" />
                </div>
                <span v-if="opened" style="color: rgba(255, 255, 255, 0.6)" class="text-xs">{{ menu.title }}</span>
                <template v-if="opened">
                    <PtIcon v-show="!expand || showTriangle" class="shrink-0 text-base !fill-eureka-200" :icon="expand ? 'SolidArrowUp' : 'SolidArrowRight' " />
                </template>
                <template v-else>
                    <Triangle class="absolute bottom-1/4 right-1/4 w-[5px] h-[5px] shrink-0 z-10 fill-white" />
                </template>
            </div>
        </ElTooltip>

        <section v-show="childrenVisible" class="flex flex-col cursor-pointer">
            <component
                :is="subComponent"
                v-for="child in menu.children"
                :key="child.id"
                :child="child"
            />
        </section>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import _ from 'lodash';
import { ElTooltip } from 'element-plus';
import PtIcon from '@patsnap-biz/icon';
import Triangle from './icons/TriangleIcon.vue';
import SubItem from './SubItem.vue';
import HistorySubItem from './HistorySubItem.vue';
import SubComponentPopover from './SubComponentPopover.vue';
import type { IMenuItem } from '../types';
import { HISTORY_KEY } from '../constants';
import '@patsnap-ui/icon/assets/solid/ArrowUp.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/home.svg';
import '@patsnap-ui/icon/assets/solid/layers.svg';
import '@patsnap-ui/icon/assets/solid/TimeRefresh.svg';
import '@patsnap-ui/icon/assets/solid/aim.svg';
import '@patsnap-ui/icon/assets/solid/SearchLeft.svg';
import '@patsnap-ui/icon/assets/menu/agent.svg';
import '@patsnap-ui/icon/assets/menu/community.svg';
import '@patsnap-ui/icon/assets/menu/customize.svg';
import '@patsnap-ui/icon/assets/menu/kg.svg';
import '@patsnap-ui/icon/assets/menu/newTask.svg';
import '@patsnap-ui/icon/assets/menu/vault.svg';

const props = defineProps<{
    menu: IMenuItem;
    opened: boolean;
    expand?: boolean;
}>();
const emits = defineEmits(['expand']);

// Triangle 显示状态
const showTriangle = ref(false);

const hasChildren = computed(() => _.size(props.menu?.children) > 0);
const childrenVisible = computed(() => hasChildren.value && props.expand && props.opened);

const isChildMenuActive = computed(() => _.some(props.menu?.children, (child) => child.active));

const parentActive = computed(() => (!props.opened && isChildMenuActive.value) || props.menu.active);

// 自定义图标映射（以 Icon 开头）
const ICON_MAP: Record<string, any> = {
    // IconNewTask,
    // IconKnowledgeBase,
    // IconCommunity,
    // IconTask,
    // IconAgent,
    // IconCustomize,
};

// 判断是否使用自定义图标组件
const customIconComponent = computed(() => {
    const icon = props.menu.icon;
    if (typeof icon === 'string' && icon.startsWith('Icon') && ICON_MAP[icon]) {
        return ICON_MAP[icon];
    }
    return null;
});

const subComponent = computed(() => {
    if (props.menu.id === HISTORY_KEY) {
        return HistorySubItem;
    }
    return SubItem;
});

const toggleMenu = () => {
    if (hasChildren.value) {
        emits('expand', props.menu.id);
    } else {
        props.menu.onClick?.(props.menu);
    }
};
</script>
